// components/masterdata/LaenderListe.jsx

import React, {useState, useEffect, useCallback} from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router';
import Navbar from '../utils/Navbar';

const LaenderListe = () => {
  const { fetchWithAuth, accessToken } = useAuth();
  const navigate = useNavigate();

  const [laender, setLaender] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [newLand, setNewLand] = useState({
    name: "",
    name_abk: "",
    name_foreign: "",
    remark: "",
  });

  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});

  const fetchLaender = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`/api/data/laender`, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Laender');
      }

      const data = await response.json();
      setLaender(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  const handleSave = async () => {
    try {
      for (const id in editData) {
        await fetchWithAuth(`/api/data/laender/${id}/`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editData[id]),
        });
      }
      setEditMode(false);
      fetchLaender();
    } catch (err) {
      alert("Fehler beim Speichern: " + err.message);
    }
  };

  const handleDeleteSingle = async (id) => {
    if (!window.confirm("Willst du diese Land wirklich löschen?")) return;

    try {
      await fetchWithAuth(`/api/data/laender/${id}/`, {
        method: "DELETE",
      });
      fetchLaender(); // Liste neu laden
    } catch (err) {
      alert("Fehler beim Löschen: " + err.message);
    }
  };

  const handleAddLand = async () => {
    if (!newLand.name.trim()) return;

    try {
      const response = await fetchWithAuth(`/api/data/laender/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newLand)
      });

      if (response.ok) {
        setNewLand({ name: "", name_abk: "", name_foreign: "", remark: "" });
        fetchLaender();
      } else {
        alert('Fehler beim Hinzufügen');
      }
    } catch (err) {
      alert('Netzwerkfehler: ' + err.message);
    }
  };

  useEffect(() => {
    if (!accessToken) return;
    fetchLaender();
  }, [accessToken, fetchLaender]);

  if (loading) return <div>Lade Laender...</div>;
  if (error) return <div className="text-danger">Fehler: {error}</div>;

  return (
    <>
      <Navbar />
      <div className="container mt-4">
        <h2 className="mb-4">Länder verwalten</h2>

        <div className="d-flex mb-4 gap-2">
          <input
            className="form-control"
            type="text"
            placeholder="Neues Land..."
            value={newLand.name}
            onChange={(e) => setNewLand({ ...newLand, name: e.target.value })}
          />
          <input
            className="form-control"
            type="text"
            placeholder="Abkürzung"
            value={newLand.name_abk}
            onChange={(e) => setNewLand({ ...newLand, name_abk: e.target.value })}
          />
          <input
            className="form-control"
            type="text"
            placeholder="Name Fremdsprache"
            value={newLand.name_foreign}
            onChange={(e) => setNewLand({ ...newLand, name_foreign: e.target.value })}
          />
          <input
            className="form-control"
            type="text"
            placeholder="Bemerkungen"
            value={newLand.remark}
            onChange={(e) => setNewLand({ ...newLand, remark: e.target.value })}
          />
          <button className="btn btn-success" onClick={handleAddLand}>
            Hinzufügen
          </button>
        </div>

        {editMode && (
          <div className="alert alert-warning d-flex align-items-center" role="alert">
            <i className="bi bi-pencil-square me-2"></i>
            Bearbeitungsmodus aktiv – Änderungen noch nicht gespeichert!
          </div>
        )}

        <table className="table table-striped">
          <thead>
            <tr>
              {editMode && <th>Aktion</th>}
              <th>ID</th>
              <th>Name</th>
              <th>Abkürzung</th>
              <th>Name in Fremdsprache</th>
              <th>Bemerkungen</th>
            </tr>
          </thead>
          <tbody>
            {laender.map((kat) => (
              <tr key={kat.id}>
                {editMode && (
                  <td>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteSingle(kat.id)}
                    >
                      🗑️
                    </button>
                  </td>
                )}
                <td>{kat.id}</td>

                {/* Name */}
                <td>
                  {editMode ? (
                    <input
                      type="text"
                      className="form-control"
                      value={editData[kat.id]?.name || ""}
                      onChange={(e) =>
                        setEditData({...editData, [kat.id]: { ...editData[kat.id], name: e.target.value }
                        })
                      }
                    />
                  ) : (
                    kat.name
                  )}
                </td>

                {/* Name Abk */}
                <td>
                  {editMode ? (
                    <input
                      type="text"
                      className="form-control"
                      value={editData[kat.id]?.name_abk || ""}
                      onChange={(e) =>
                        setEditData({...editData, [kat.id]: { ...editData[kat.id], name_abk: e.target.value }
                        })
                      }
                    />
                  ) : (
                    kat.name_abk
                  )}
                </td>

                {/* Name Fremdsprache */}
                <td>
                  {editMode ? (
                    <input
                      type="text"
                      className="form-control"
                      value={editData[kat.id]?.name_foreign || ""}
                      onChange={(e) =>
                        setEditData({...editData, [kat.id]: { ...editData[kat.id], name_abk: e.target.value }
                        })
                      }
                    />
                  ) : (
                    kat.name_foreign
                  )}
                </td>

                {/* Anmerkungen */}
                <td>
                  {editMode ? (
                    <input
                      type="text"
                      className="form-control"
                      value={editData[kat.id]?.remark || ""}
                      onChange={(e) =>
                        setEditData({...editData, [kat.id]: { ...editData[kat.id], remark: e.target.value }
                        })
                      }
                    />
                  ) : (
                    kat.remark
                  )}
                </td>

              </tr>
            ))}
          </tbody>
        </table>

        <div className="d-flex flex-wrap gap-2 mb-4">
          <button className="btn btn-secondary" onClick={() => navigate("/objekte")}>
            Zurück
          </button>

          {!editMode && (
            <button className="btn btn-primary" onClick={() => {
              setEditMode(true);
              const initialData = {};
              laender.forEach(k => {
                initialData[k.id] = {
                  name: k.name,
                  name_abk: k.name_abk,
                  name_foreign: k.name_foreign,
                  remark: k.remark,
                };
              });
              setEditData(initialData);
            }}>
              Bearbeiten
            </button>
          )}

          {editMode && (
              <button className="btn btn-success" onClick={handleSave}>
                Änderungen speichern
              </button>
          )}
        </div>
      </div>
    </>
  );
};

export default LaenderListe;
